import { ChartSvg, ChartTitle } from "@/components/tool-kit/charts/chart-svg";
import { px } from "@/components/tool-kit/charts/marks";
import { Arrow, TextLines } from "@/components/tool-kit/diagrams/svg-parts";
import { LINE_HEIGHT } from "@/lib/tool-kit/charts/layout";
import { mixHex, tint } from "@/lib/tool-kit/diagrams/color";
import {
  type ArgumentKind,
  type ArgumentLayout,
  BODY_FONT,
  TAG_FONT,
} from "@/lib/tool-kit/diagrams-more/argument-map";
import { titledFrame } from "@/lib/tool-kit/diagrams-more/frame";
import { CHART_COLORS, CHART_INK } from "@/lib/tool-kit/palette";

/*
 * An argument map from `layoutArgumentMap`. Each box carries a tag — Claim, Reason,
 * Objection or Rebuttal — and bears on the box its arrow points at. Reasons are bluish
 * green with solid arrows; objections and rebuttals are vermillion with dashed arrows
 * and dashed borders, so the two stay apart without colour. The main claim is dark.
 */

const SUPPORT = CHART_COLORS[2];
const OPPOSE = CHART_COLORS[3];

function accent(kind: ArgumentKind): string {
  if (kind === "reason") {
    return SUPPORT;
  }
  return kind === "claim" ? CHART_INK.text : OPPOSE;
}

export function ArgumentMapDiagram({
  id,
  layout,
  tags,
  title,
  label,
  description,
}: {
  id: string;
  layout: ArgumentLayout;
  tags: Record<ArgumentKind, string>;
  title: string;
  label: string;
  description: string;
}) {
  const frame = titledFrame(layout.width, layout.height, title);
  const tagHeight = TAG_FONT * LINE_HEIGHT;
  const bodyHeight = BODY_FONT * LINE_HEIGHT;
  return (
    <ChartSvg
      description={description}
      height={frame.height}
      id={id}
      label={label}
      width={frame.width}
    >
      <ChartTitle title={frame.title} />
      <g transform={`translate(${px(frame.dx)} ${px(frame.dy)})`}>
        {layout.arrows.map((arrow) => (
          <Arrow
            color={arrow.stance === "support" ? SUPPORT : OPPOSE}
            dashed={arrow.stance === "oppose"}
            head={9}
            key={`arrow-${arrow.from}`}
            points={arrow.points}
            width={2}
          />
        ))}
        {layout.nodes.map((entry) => {
          const kind = entry.node.kind;
          const color = accent(kind);
          const claim = kind === "claim";
          const textHeight = tagHeight + 3 + entry.lines.length * bodyHeight;
          const top = entry.rect.y + (entry.rect.height - textHeight) / 2;
          const left = entry.rect.x + 10;
          return (
            <g key={`box-${entry.node.id}`}>
              <rect
                fill={claim ? CHART_INK.text : tint(color, 0.92)}
                height={px(entry.rect.height)}
                rx={6}
                stroke={color}
                strokeDasharray={
                  kind === "objection" || kind === "rebuttal"
                    ? "5 3"
                    : undefined
                }
                strokeWidth={1.75}
                width={px(entry.rect.width)}
                x={px(entry.rect.x)}
                y={px(entry.rect.y)}
              />
              <TextLines
                anchor="start"
                bold
                fill={claim ? "#ffffff" : mixHex(color, "#000000", 0.4)}
                fontSize={TAG_FONT}
                lineHeight={tagHeight}
                lines={[`${entry.node.number} ${tags[kind]}`.toUpperCase()]}
                top={top}
                x={left}
              />
              <TextLines
                anchor="start"
                fill={claim ? "#ffffff" : CHART_INK.text}
                fontSize={BODY_FONT}
                lineHeight={bodyHeight}
                lines={entry.lines}
                top={top + tagHeight + 3}
                x={left}
              />
            </g>
          );
        })}
      </g>
    </ChartSvg>
  );
}
